import React, { useState, useEffect, useCallback, useMemo, useContext } from 'react';
import { Typography, Form, DatePicker, Select, Button, Table, Tag, Space, Spin, Empty, Progress, Card, Popover, Modal, List, Drawer, Segmented } from 'antd';
import { TeamOutlined, AuditOutlined, PlusSquareOutlined, MinusSquareOutlined, EyeOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { UserContext } from '../App';
import api from '../api';
import EvalClientReport from '../components/EvalClientReport';

const { Title, Text } = Typography;

function scoreColor(pct) {
  if (pct == null) return '#8c8c8c';
  if (pct >= 80) return '#52c41a';
  if (pct >= 60) return '#faad14';
  return '#ff4d4f';
}

function CountCell({ value, color }) {
  if (!value) return <span style={{ color: '#bfbfbf' }}>—</span>;
  return <Tag color={color}>{value}</Tag>;
}

function ScoreCell({ value }) {
  if (value == null) return <span style={{ color: '#bfbfbf' }}>—</span>;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <Progress percent={value} showInfo={false} strokeColor={scoreColor(value)} size="small" style={{ flex: 1, minWidth: 50 }} />
      <Text strong style={{ color: scoreColor(value), whiteSpace: 'nowrap', fontSize: 12 }}>{value}%</Text>
    </div>
  );
}

function buildCampaignCols(onStatusClick) {
  function clickable(dataIndex, color, status) {
    return (v, camp) => {
      if (!v) return <span style={{ color: '#bfbfbf' }}>—</span>;
      return (
        <div style={{ cursor: 'pointer', display: 'inline-block' }} onClick={() => onStatusClick(camp, status)}>
          <Tag color={color}>{v}</Tag>
        </div>
      );
    };
  }
  return [
    {
      title: 'Campaign',
      dataIndex: 'Campaign',
      key: 'Campaign',
      width: 200,
      render: (v) => <Text style={{ fontSize: 12 }}>{v}</Text>,
    },
    {
      title: 'Avg Score',
      dataIndex: 'AvgScore',
      key: 'AvgScore',
      width: 160,
      render: (v) => <ScoreCell value={v} />,
    },
    {
      title: 'Target (Gold)',
      dataIndex: 'GoalTargetPct',
      key: 'GoalTargetPct',
      width: 120,
      align: 'right',
      render: (v) => v != null ? `${v}%` : <span style={{ color: '#bfbfbf' }}>—</span>,
    },
    {
      title: 'Completed',
      dataIndex: 'completed',
      key: 'completed',
      width: 100,
      align: 'center',
      render: (v, camp) => {
        if (!v) return <span style={{ color: '#bfbfbf' }}>—</span>;
        return (
          <div style={{ cursor: 'pointer', display: 'inline-block' }} onClick={() => onStatusClick(camp, 'completed')}>
            <Tag color="green">{v}</Tag>
            {camp.MaxCallDate && (
              <div style={{ fontSize: 10, color: '#8c8c8c', marginTop: 2, lineHeight: 1 }}>
                {camp.MaxCallDate}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: 'Min Req.',
      dataIndex: 'MinReq',
      key: 'MinReq',
      width: 80,
      align: 'center',
      render: (v, r) => {
        if (!v) return <span style={{ color: '#bfbfbf' }}>—</span>;
        const isShort = r.completed < v;
        return <Tag color={isShort ? 'red' : 'default'}>{v}</Tag>;
      },
    },
    { title: 'Rescore',     dataIndex: 'rescore_requested', key: 'rescore_requested', width: 75, align: 'center', render: clickable('rescore_requested', 'purple',  'rescore_requested') },
    { title: 'Draft',       dataIndex: 'assigned',          key: 'assigned',          width: 70, align: 'center', render: clickable('assigned',          'default', 'assigned') },
    { title: 'In Progress', dataIndex: 'in_progress',       key: 'in_progress',       width: 90, align: 'center', render: clickable('in_progress',       'blue',    'in_progress') },
    {
      title: 'Inbound Calls',
      dataIndex: 'InboundCalls',
      key: 'InboundCalls',
      width: 115,
      align: 'right',
      render: (v) => v > 0 ? <Text style={{ fontSize: 12, fontVariantNumeric: 'tabular-nums' }}>{v.toLocaleString()}</Text> : <span style={{ color: '#bfbfbf' }}>—</span>,
    },
    {
      title: 'Completed Feedback Only',
      dataIndex: 'feedback_only_completed',
      key: 'feedback_only_completed',
      width: 170,
      align: 'center',
      render: (v) => <CountCell value={v} color="cyan" />,
    },
  ];
}

function agentNameCol(navigate, allExpanded, toggleExpand, currentUser) {
  return {
    title: (
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span>Agent</span>
        <Button
          size="small"
          type="text"
          icon={allExpanded ? <MinusSquareOutlined /> : <PlusSquareOutlined />}
          onClick={(e) => { e.stopPropagation(); toggleExpand(); }}
          title={allExpanded ? 'Collapse all' : 'Expand all'}
          style={{ padding: '0 2px', height: 18, lineHeight: 1 }}
        />
      </div>
    ),
    dataIndex: 'DisplayName',
    key: 'DisplayName',
    width: 220,
    sorter: (a, b) => {
      // Active agents sort before inactive
      if ((a.IsActive ?? 1) !== (b.IsActive ?? 1)) return (b.IsActive ?? 1) - (a.IsActive ?? 1);
      return (a.DisplayName ?? '').localeCompare(b.DisplayName ?? '');
    },
    defaultSortOrder: 'ascend',
    render: (name, r) => {
      const lower = (r.PaycorStatus || '').toLowerCase();
      const statusColor = lower === 'active' ? '#52c41a' : lower === 'terminated' ? '#ff4d4f' : '#8c8c8c';
      const isInactive = r.IsActive === 0;
      return (
        <div>
          <Space size={4} align="center">
            {(currentUser?.CanViewAgentSummary ?? 0) >= 1
              ? <Typography.Link onClick={() => navigate(`/agents?user_id=${encodeURIComponent(r.UserID)}`)}>{name}</Typography.Link>
              : <span>{name}</span>
            }
            {isInactive && <Tag color="red" style={{ fontSize: 10, lineHeight: '16px', padding: '0 4px' }}>Inactive</Tag>}
          </Space>
          {(r.PaycorStatus || r.PaycorHireDate) && (
            <div style={{ fontSize: 12, color: '#595959', lineHeight: 1.5, marginTop: 2 }}>
              {r.PaycorStatus && <span style={{ color: statusColor }}>{r.PaycorStatus}</span>}
              {r.PaycorStatus && r.PaycorHireDate && <span> · </span>}
              {r.PaycorHireDate && <span>{r.PaycorHireDate}</span>}
            </div>
          )}
        </div>
      );
    },
  };
}

// Full Time / Part Time from Paycor (AgentFullTime); no Paycor record = blank.
const ftLabel = (v) => (v == null ? null : (v ? 'Full Time' : 'Part Time'));
const FT_COL = {
  title: 'FT / PT',
  key: 'FullTime',
  width: 100,
  sorter: (a, b) => (ftLabel(a.AgentFullTime) ?? '').localeCompare(ftLabel(b.AgentFullTime) ?? ''),
  render: (_, r) => ftLabel(r.AgentFullTime) || <span style={{ color: '#bfbfbf' }}>—</span>,
};

const JOB_COL = {
  title: 'Job',
  dataIndex: 'JobName',
  key: 'JobName',
  width: 130,
  sorter: (a, b) => (a.JobName ?? '').localeCompare(b.JobName ?? ''),
  defaultSortOrder: 'ascend',
  render: (v) => v ? <Text style={{ fontSize: 12 }}>{v}</Text> : <span style={{ color: '#bfbfbf' }}>—</span>,
};

const AGENT_COLS_STATIC = [
  {
    title: 'Avg Score',
    dataIndex: 'AvgScore',
    key: 'AvgScore',
    width: 160,
    sorter: (a, b) => (a.AvgScore ?? -1) - (b.AvgScore ?? -1),
    render: (v) => <ScoreCell value={v} />,
  },
  {
    title: 'Target (Gold)',
    dataIndex: 'GoalTargetPct',
    key: 'GoalTargetPct',
    width: 120,
    align: 'right',
    sorter: (a, b) => (a.GoalTargetPct ?? -1) - (b.GoalTargetPct ?? -1),
    render: (v) => v != null ? `${v}%` : <span style={{ color: '#bfbfbf' }}>—</span>,
  },
  // Completed replaced dynamically below (needs handleOpenEvals callback)
  { _placeholder: 'Completed', title: 'Completed', dataIndex: 'completed', key: 'completed', width: 100, align: 'center', sorter: (a, b) => a.completed - b.completed },
  {
    title: 'Min Req.',
    dataIndex: 'MinReq',
    key: 'MinReq',
    width: 80,
    align: 'center',
    sorter: (a, b) => (a.MinReq || 0) - (b.MinReq || 0),
    render: (v, r) => {
      if (!v) return <span style={{ color: '#bfbfbf' }}>—</span>;
      const isShort = r.completed < v;
      return <Tag color={isShort ? 'red' : 'default'}>{v}</Tag>;
    },
  },
  { _placeholder: 'rescore_requested', title: 'Rescore',      dataIndex: 'rescore_requested', key: 'rescore_requested', width: 75, align: 'center', sorter: (a, b) => a.rescore_requested - b.rescore_requested },
  { _placeholder: 'assigned',          title: 'Draft',        dataIndex: 'assigned',          key: 'assigned',          width: 70, align: 'center', sorter: (a, b) => a.assigned - b.assigned },
  { _placeholder: 'in_progress',       title: 'In Progress',  dataIndex: 'in_progress',       key: 'in_progress',       width: 90, align: 'center', sorter: (a, b) => a.in_progress - b.in_progress },
  // TotalInbound replaced dynamically below (needs openCondModal callback)
  { _placeholder: 'TotalInbound', title: 'Total Inbound', dataIndex: 'TotalInbound', key: 'TotalInbound', width: 115, align: 'right', sorter: (a, b) => a.TotalInbound - b.TotalInbound },
  {
    title: 'Completed Feedback Only',
    dataIndex: 'FeedbackOnlyCompleted',
    key: 'FeedbackOnlyCompleted',
    width: 170,
    align: 'center',
    sorter: (a, b) => a.FeedbackOnlyCompleted - b.FeedbackOnlyCompleted,
    render: (v) => <CountCell value={v} color="cyan" />,
  },
];

const SUMMARY_CARDS = [
  { title: 'Avg Score %',     key: 'AvgScore',           fmt: (v) => v != null ? `${v}%` : '—', color: (v) => v != null ? scoreColor(v) : '#bfbfbf' },
  { title: 'Completed Evals', key: 'completed',          fmt: (v) => v || '—', color: () => '#52c41a' },
  { title: 'Draft Evals',     key: 'assigned',           fmt: (v) => v || '—', color: () => 'var(--ant-color-text)' },
  { title: 'Rescore Evals',   key: 'rescore_requested',  fmt: (v) => v || '—', color: () => '#722ed1' },
  { title: 'In Progress',     key: 'in_progress',        fmt: (v) => v || '—', color: () => '#1677ff' },
];

function SummaryCards({ campaignSummary }) {
  if (!campaignSummary.length) return null;
  return (
    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
      {SUMMARY_CARDS.map(({ title, key, fmt, color }) => (
        <Card key={key} size="small" title={<Text style={{ fontSize: 12, fontWeight: 600 }}>{title}</Text>}
          styles={{ body: { padding: '6px 12px' }, header: { minHeight: 36, padding: '0 12px' } }}
          style={{ minWidth: 160, flex: '1 1 160px', maxWidth: 280 }}
        >
          {campaignSummary.map((camp) => (
            <div key={camp.Campaign} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '2px 0', borderBottom: '1px solid var(--ant-color-split)' }}>
              <Text style={{ fontSize: 11, flex: 1, marginRight: 8, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={camp.Campaign}>
                {camp.Campaign}
              </Text>
              <Text strong style={{ fontSize: 12, whiteSpace: 'nowrap', color: color(camp[key]) }}>
                {fmt(camp[key])}
              </Text>
            </div>
          ))}
        </Card>
      ))}
    </div>
  );
}

function CalendarView({ rows, month, agentInfoMap = {}, currentUser }) {
  const navigate = useNavigate();
  if (!rows.length) {
    return <Empty description="No eval data for this period." style={{ marginTop: 40 }} />;
  }

  const daysInMonth = month ? month.daysInMonth() : 31;

  const sorted = [...rows].sort((a, b) => {
    const j = (a.JobName ?? '').localeCompare(b.JobName ?? '');
    if (j !== 0) return j;
    const n = (a.DisplayName ?? '').localeCompare(b.DisplayName ?? '');
    if (n !== 0) return n;
    return (a.Campaign ?? '').localeCompare(b.Campaign ?? '');
  });

  // Pre-compute row spans so the Agent cell merges across its campaign rows
  const agentFirstIdx = {};
  const agentSpan = {};
  sorted.forEach((row, idx) => {
    const key = row.AgentUserID;
    if (agentFirstIdx[key] === undefined) {
      agentFirstIdx[key] = idx;
      agentSpan[key] = 0;
    }
    agentSpan[key]++;
  });

  const dayCols = Array.from({ length: daysInMonth }, (_, i) => {
    const d = i + 1;
    return {
      title: <span style={{ fontSize: 11, fontWeight: 500 }}>{d}</span>,
      key: `day_${d}`,
      width: 42,
      align: 'center',
      render: (_, r) => {
        const dd = r.days?.[d];
        if (!dd || (dd.completed === 0 && dd.other === 0)) {
          return <span style={{ color: '#d9d9d9' }}>·</span>;
        }
        return (
          <div style={{ lineHeight: 1.3 }}>
            {dd.completed > 0 && (
              <div style={{ color: '#52c41a', fontWeight: 700, fontSize: 13 }}>{dd.completed}</div>
            )}
            {dd.other > 0 && (
              <div style={{ color: '#faad14', fontSize: 10 }}>{dd.other}</div>
            )}
          </div>
        );
      },
    };
  });

  const columns = [
    {
      title: 'Agent',
      dataIndex: 'DisplayName',
      key: 'DisplayName',
      width: 200,
      fixed: 'left',
      render: (v, r, rowIdx) => {
        const key = r.AgentUserID;
        if (rowIdx !== agentFirstIdx[key]) return { props: { rowSpan: 0 } };
        const info = agentInfoMap[key] || r;
        const lower = (info.PaycorStatus || '').toLowerCase();
        const statusColor = lower === 'active' ? '#52c41a' : lower === 'terminated' ? '#ff4d4f' : '#8c8c8c';
        const isInactive = info.IsActive === 0;
        return {
          children: (
            <div>
              <Space size={4} align="center">
                {(currentUser?.CanViewAgentSummary ?? 0) >= 1
                  ? <Typography.Link onClick={() => navigate(`/agents?user_id=${encodeURIComponent(r.AgentUserID)}`)}>{v}</Typography.Link>
                  : <span>{v}</span>
                }
                {isInactive && <Tag color="red" style={{ fontSize: 10, lineHeight: '16px', padding: '0 4px' }}>Inactive</Tag>}
              </Space>
              {(info.PaycorStatus || info.PaycorHireDate) && (
                <div style={{ fontSize: 12, color: '#595959', lineHeight: 1.5, marginTop: 2 }}>
                  {info.PaycorStatus && <span style={{ color: statusColor }}>{info.PaycorStatus}</span>}
                  {info.PaycorStatus && info.PaycorHireDate && <span> · </span>}
                  {info.PaycorHireDate && <span>{info.PaycorHireDate}</span>}
                </div>
              )}
            </div>
          ),
          props: { rowSpan: agentSpan[key] },
        };
      },
    },
    {
      // One cell per agent, like Agent; not sortable here, since sorting
      // would split an agent's campaign rows.
      title: 'FT / PT',
      key: 'FullTime',
      width: 100,
      fixed: 'left',
      render: (_, r, rowIdx) => {
        const key = r.AgentUserID;
        if (rowIdx !== agentFirstIdx[key]) return { props: { rowSpan: 0 } };
        const info = agentInfoMap[key] || r;
        return {
          children: ftLabel(info.AgentFullTime) || <span style={{ color: '#bfbfbf' }}>—</span>,
          props: { rowSpan: agentSpan[key] },
        };
      },
    },
    {
      title: 'Campaign',
      dataIndex: 'Campaign',
      key: 'Campaign',
      width: 180,
      fixed: 'left',
      render: (v, r) => (
        <div>
          <Text style={{ fontSize: 12, color: v ? undefined : '#bfbfbf' }}>{v ?? '—'}</Text>
          {r.MaxCallDate && (
            <div style={{ fontSize: 10, color: '#8c8c8c', marginTop: 2, lineHeight: 1 }}>
              Last Call: {r.MaxCallDate}
            </div>
          )}
        </div>
      ),
    },
    ...dayCols,
    {
      title: 'Total',
      key: 'total',
      width: 65,
      align: 'center',
      fixed: 'right',
      render: (_, r) => (
        <div style={{ lineHeight: 1.3 }}>
          {r.TotalCompleted > 0 && (
            <div style={{ color: '#52c41a', fontWeight: 700 }}>{r.TotalCompleted}</div>
          )}
          {r.TotalOther > 0 && (
            <div style={{ color: '#faad14', fontSize: 11 }}>{r.TotalOther}</div>
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      <div style={{ marginBottom: 6 }}>
        <Text style={{ fontSize: 13, fontWeight: 600 }}>Number of Evaluations based on Call Log Date</Text>
      </div>
      <Space style={{ marginBottom: 10 }}>
        <Space size={4}>
          <span style={{ display: 'inline-block', width: 10, height: 10, background: '#52c41a', borderRadius: 2 }} />
          <Text style={{ fontSize: 12 }}>Completed</Text>
        </Space>
        <Space size={4} style={{ marginLeft: 8 }}>
          <span style={{ display: 'inline-block', width: 10, height: 10, background: '#faad14', borderRadius: 2 }} />
          <Text style={{ fontSize: 12 }}>Draft / In Progress / Rescore</Text>
        </Space>
      </Space>
      <Table
        rowKey={(r) => `${r.AgentUserID}_${r.CampaignID}`}
        columns={columns}
        dataSource={sorted}
        size="small"
        pagination={false}
        scroll={{ x: 'max-content' }}
        sticky
        bordered
      />
    </>
  );
}

export default function EvalProgressPage() {
  const navigate                  = useNavigate();
  const currentUser               = useContext(UserContext);
  const [form]                    = Form.useForm();
  const [teams, setTeams]         = useState([]);
  const [jobs,  setJobs]          = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [rows,     setRows]     = useState([]);
  const [calRows,  setCalRows]  = useState([]);
  // Client Report: the searched campaigns' evals for the month (null when no
  // campaign was picked -- the report needs at least one).
  const [clientReport, setClientReport] = useState(null);
  const [reportMonth,  setReportMonth]  = useState(null);
  const [reportCampaignIds, setReportCampaignIds] = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [searched, setSearched] = useState(false);
  const [activeView, setActiveView]             = useState('counts');
  const [expandedRowKeys, setExpandedRowKeys]   = useState([]);

  // "Find Qualified Calls" modal state
  const [condModal,   setCondModal]   = useState(false);
  const [condAgent,   setCondAgent]   = useState(null);
  const [conditions,  setConditions]  = useState([]);
  const [condLoading, setCondLoading] = useState(false);

  // Eval list drawer state
  const [evalDrawerOpen, setEvalDrawerOpen] = useState(false);
  const [evalContext,    setEvalContext]    = useState(null);
  const [evalList,       setEvalList]       = useState([]);
  const [evalLoading,    setEvalLoading]    = useState(false);

  useEffect(() => {
    api.get('/api/teams?my_teams=1&no_client_users=1').then((r) => setTeams(r.data.map((t) => ({ label: t.Name, value: t.ID })))).catch(() => {});
    api.get('/api/jobs').then((r)  => setJobs(r.data.map((j)  => ({ label: j.JobName, value: j.JobID })))).catch(() => {});
    api.get('/api/agent-stats/options').then((r) => setCampaigns((r.data.campaigns || []).map((c) => ({ label: c.Name, value: c.ID })))).catch(() => {});
  }, []);

  const handleSearch = useCallback(async () => {
    const values = form.getFieldsValue();
    if (!values.team_id || !values.month) return;

    setLoading(true);
    setRows([]);
    setCalRows([]);
    setClientReport(null);
    setSearched(true);
    try {
      const params = {
        team_id: values.team_id,
        month:   values.month.format('YYYY-MM'),
      };
      if (values.job_ids?.length)      params.job_ids      = values.job_ids.join(',');
      if (values.campaign_ids?.length) params.campaign_ids = values.campaign_ids.join(',');
      if (values.hired_before)         params.hired_before = values.hired_before.format('YYYY-MM-DD');

      const reportCamps = values.campaign_ids || [];
      const [evalRes, calRes, reportRes] = await Promise.allSettled([
        api.get('/api/eval-progress', { params }),
        api.get('/api/eval-progress/calendar', { params }),
        reportCamps.length
          ? api.get('/api/eval-progress/client-report', { params })
          : Promise.resolve(null),
      ]);
      setClientReport(reportRes.status === 'fulfilled' && reportRes.value ? reportRes.value.data : null);
      setReportMonth(values.month);
      setReportCampaignIds(reportCamps);

      if (evalRes.status === 'fulfilled') {
        const data = Array.isArray(evalRes.value.data) ? evalRes.value.data : [];
        data.sort((a, b) => {
          const j = (a.JobName ?? '').localeCompare(b.JobName ?? '');
          return j !== 0 ? j : (a.DisplayName ?? '').localeCompare(b.DisplayName ?? '');
        });
        setRows(data);
        setExpandedRowKeys(data.filter((r) => r.campaigns.length > 1).map((r) => r.UserID));
      } else {
        setRows([]);
      }

      setCalRows(calRes.status === 'fulfilled' ? (Array.isArray(calRes.value.data) ? calRes.value.data : []) : []);
    } finally {
      setLoading(false);
    }
  }, [form]);

  const allExpanded = rows.length > 0 && expandedRowKeys.length === rows.length;

  const toggleExpand = useCallback(() => {
    setExpandedRowKeys(allExpanded ? [] : rows.map((r) => r.UserID));
  }, [allExpanded, rows]);

  const openCondModal = useCallback((agent) => {
    setCondAgent(agent);
    setConditions([]);
    setCondModal(true);
    const campaignIds = (agent.campaigns || []).map((c) => c.CampaignID).filter((id) => id > 0);
    if (!campaignIds.length) return;
    setCondLoading(true);
    api.get('/api/eval-call-conditions/active', { params: { campaign_ids: campaignIds.join(',') } })
      .then((r) => setConditions(r.data))
      .catch(() => setConditions([]))
      .finally(() => setCondLoading(false));
  }, []);

  const handleOpenSearch = useCallback((cond) => {
    const agent = condAgent;
    const agentNos = (agent?.AgentNos || []).map((n) => String(n));
    const month = form.getFieldValue('month');
    const startDate = (month || dayjs()).startOf('month').format('YYYY-MM-DD');
    const endDate   = (month || dayjs()).endOf('month').format('YYYY-MM-DD');

    const preset = { dateRange: [startDate, endDate] };
    if (agentNos.length) preset.agent_name = agentNos;
    if (cond) {
      if (cond.CampaignCxNo)          preset.campaign      = [cond.CampaignCxNo];
      if (cond.SkillName)             preset.skill         = [cond.SkillName];
      if (cond.Direction)             preset.direction     = [cond.Direction.charAt(0).toUpperCase() + cond.Direction.slice(1).toLowerCase()];
      if (cond.MediaNameList?.length) preset.media_name    = cond.MediaNameList;
      if (cond.MinAgentTime != null)         preset.agent_time_min       = cond.MinAgentTime;
      if (cond.MaxAgentTime != null)         preset.agent_time_max       = cond.MaxAgentTime;
      if (cond.MinActiveTalkTime != null)    preset.active_talk_time_min = cond.MinActiveTalkTime;
      if (cond.MaxActiveTalkTime != null)    preset.active_talk_time_max = cond.MaxActiveTalkTime;
      if (cond.ContactTypeList?.length)      preset.contact_type         = cond.ContactTypeList;
    }

    localStorage.setItem('qa_search_preset', JSON.stringify({ data: preset, ts: Date.now() }));
    window.open('/search?from_preset=1', '_blank');
    setCondModal(false);
  }, [condAgent, form]);

  const STATUS_LABEL = { completed: 'Completed', assigned: 'Draft', in_progress: 'In Progress', rescore_requested: 'Rescore' };

  const handleOpenEvals = useCallback((agent, camp, status = null) => {
    const month = form.getFieldValue('month');
    setEvalContext({
      agentUserID:  agent.UserID,
      agentName:    agent.DisplayName,
      campaign:     camp?.Campaign ?? null,
      campaignId:   camp?.CampaignID ?? null,
      monthLabel:   month ? month.format('MMMM YYYY') : '',
      statusLabel:  status ? STATUS_LABEL[status] : null,
    });
    setEvalList([]);
    setEvalLoading(true);
    setEvalDrawerOpen(true);
    const params = {
      agent_user_id: agent.UserID,
      month: month ? month.format('YYYY-MM') : '',
    };
    if (camp != null) params.campaign_id = camp.CampaignID;
    if (status)       params.status = status;
    api.get('/api/eval-progress/evals', { params })
      .then((r) => setEvalList(r.data))
      .catch(() => setEvalList([]))
      .finally(() => setEvalLoading(false));
  }, [form]);

  const agentCol = useMemo(
    () => agentNameCol(navigate, allExpanded, toggleExpand, currentUser),
    [navigate, allExpanded, toggleExpand, currentUser] // eslint-disable-line
  );

  const tableColumns = useMemo(() => {
    const inboundCol = {
      title: 'Total Inbound',
      dataIndex: 'TotalInbound',
      key: 'TotalInbound',
      width: 115,
      align: 'right',
      sorter: (a, b) => a.TotalInbound - b.TotalInbound,
      render: (v, record) => {
        if (!v) return <span style={{ color: '#bfbfbf' }}>—</span>;
        return (
          <Popover
            content={
              <Typography.Link style={{ fontSize: 12 }} onClick={() => openCondModal(record)}>
                Find Qualified Calls
              </Typography.Link>
            }
            trigger="hover"
          >
            <span style={{ cursor: 'default' }}>
              <Text style={{ fontVariantNumeric: 'tabular-nums' }}>{v.toLocaleString()}</Text>
            </span>
          </Popover>
        );
      },
    };
    const makeStatusCol = (placeholder, title, color, width, status) => ({
      title,
      dataIndex: placeholder,
      key: placeholder,
      width,
      align: 'center',
      sorter: (a, b) => (a[placeholder] || 0) - (b[placeholder] || 0),
      render: (v, r) => {
        if (!v) return <span style={{ color: '#bfbfbf' }}>—</span>;
        const isCompleted = status === 'completed';
        return (
          <div style={{ cursor: 'pointer', display: 'inline-block' }}
               onClick={() => handleOpenEvals(r, null, status)}>
            <Tag color={color}>{v}</Tag>
            {isCompleted && r.MaxCallDate && (
              <div style={{ fontSize: 10, color: '#8c8c8c', marginTop: 2, lineHeight: 1 }}>
                {r.MaxCallDate}
              </div>
            )}
          </div>
        );
      },
    });
    const cols = AGENT_COLS_STATIC.map((c) => {
      if (c._placeholder === 'TotalInbound')       return inboundCol;
      if (c._placeholder === 'Completed')          return makeStatusCol('completed',          'Completed',   'green',   100, 'completed');
      if (c._placeholder === 'rescore_requested')  return makeStatusCol('rescore_requested',  'Rescore',     'purple',   75, 'rescore_requested');
      if (c._placeholder === 'assigned')           return makeStatusCol('assigned',           'Draft',       'default',  70, 'assigned');
      if (c._placeholder === 'in_progress')        return makeStatusCol('in_progress',        'In Progress', 'blue',     90, 'in_progress');
      return c;
    });
    return [JOB_COL, agentCol, FT_COL, ...cols];
  }, [agentCol, openCondModal, handleOpenEvals]);

  const agentInfoMap = useMemo(() => {
    const map = {};
    rows.forEach((r) => { map[r.UserID] = r; });
    return map;
  }, [rows]);

  const campaignSummary = useMemo(() => {
    const byName = {};
    rows.forEach((agent) => {
      agent.campaigns.forEach((camp) => {
        if (!byName[camp.Campaign]) {
          byName[camp.Campaign] = { Campaign: camp.Campaign, completed: 0, assigned: 0, in_progress: 0, rescore_requested: 0, _scoreSum: 0, _scoreCount: 0 };
        }
        const c = byName[camp.Campaign];
        c.completed          += camp.completed;
        c.assigned           += camp.assigned;
        c.in_progress        += camp.in_progress;
        c.rescore_requested  += camp.rescore_requested;
        if (camp.AvgScore != null && camp.completed > 0) {
          c._scoreSum   += camp.AvgScore * camp.completed;
          c._scoreCount += camp.completed;
        }
      });
    });
    return Object.values(byName)
      .sort((a, b) => a.Campaign.localeCompare(b.Campaign))
      .map((c) => ({ ...c, AvgScore: c._scoreCount > 0 ? Math.round(c._scoreSum / c._scoreCount * 10) / 10 : null }));
  }, [rows]);

  return (
    <div>
      <Title level={4} style={{ marginTop: 0, marginBottom: 16 }}>
        <AuditOutlined style={{ marginRight: 8 }} />Eval Progress
      </Title>

      <Form form={form} layout="inline" style={{ marginBottom: 16, gap: 8, flexWrap: 'wrap' }}>
        <Form.Item name="team_id" label="Team" rules={[{ required: true }]}>
          <Select
            placeholder="Select team"
            options={teams}
            style={{ width: 240 }}
            showSearch
            filterOption={(input, opt) => (opt?.label ?? '').toLowerCase().includes(input.toLowerCase())}
          />
        </Form.Item>
        <Form.Item name="job_ids" label="Job">
          <Select
            mode="multiple"
            placeholder="Any job"
            options={jobs}
            style={{ width: 260 }}
            allowClear
            showSearch
            filterOption={(input, opt) => (opt?.label ?? '').toLowerCase().includes(input.toLowerCase())}
          />
        </Form.Item>
        <Form.Item name="campaign_ids" label="Campaign">
          <Select
            mode="multiple"
            placeholder="All campaigns"
            options={campaigns}
            style={{ width: 280 }}
            allowClear
            showSearch
            filterOption={(input, opt) => (opt?.label ?? '').toLowerCase().includes(input.toLowerCase())}
          />
        </Form.Item>
        <Form.Item
          name="month"
          label="Month"
          initialValue={dayjs().startOf('month')}
          rules={[{ required: true }]}
        >
          <DatePicker picker="month" format="MMMM YYYY" style={{ width: 160 }} />
        </Form.Item>
        {/* Optional: leaves out agents hired (or rehired) after this date. */}
        <Form.Item name="hired_before" label="Hired Before"
          tooltip="Employees hired after this date will be excluded">
          <DatePicker style={{ width: 150 }} allowClear />
        </Form.Item>
        <Form.Item>
          <Button type="primary" icon={<TeamOutlined />} onClick={handleSearch} loading={loading}>
            Search
          </Button>
        </Form.Item>
      </Form>

      {loading && (
        <div style={{ textAlign: 'center', paddingTop: 60 }}>
          <Spin size="large" />
        </div>
      )}

      {searched && (
        <Segmented
          value={activeView}
          onChange={setActiveView}
          disabled={loading}
          style={{ marginBottom: 16 }}
          options={[
            { label: 'Eval Counts', value: 'counts' },
            { label: 'Calendar View', value: 'calendar' },
            { label: 'Client Report', value: 'client' },
          ]}
        />
      )}

      {!loading && activeView === 'counts' && searched && rows.length === 0 && (
        <Empty description="No agents found for the selected filters." style={{ marginTop: 40 }} />
      )}

      {!loading && activeView === 'counts' && rows.length > 0 && (
        <>
        <SummaryCards campaignSummary={campaignSummary} />
        <Table
          rowKey="UserID"
          columns={tableColumns}
          dataSource={rows}
          size="small"
          pagination={false}
          sticky
          scroll={{ x: 'max-content' }}
          onRow={(record) => record.IsActive === 0 ? { style: { opacity: 0.65 } } : {}}
          expandable={{
            expandedRowKeys,
            onExpandedRowsChange: setExpandedRowKeys,
            expandedRowRender: (record) => (
              record.campaigns.length === 0 ? (
                <div style={{ padding: '8px 48px', color: 'var(--ant-color-text-secondary)', fontSize: 12 }}>
                  No evaluations or call data found for this month.
                </div>
              ) : (
                <div style={{ padding: '4px 48px 8px' }}>
                  <Table
                    rowKey="Campaign"
                    columns={buildCampaignCols((camp, status) => handleOpenEvals(record, camp, status))}
                    dataSource={record.campaigns}
                    size="small"
                    pagination={false}
                  />
                </div>
              )
            ),
            rowExpandable: () => true,
          }}
        />
        </>
      )}

      {!loading && activeView === 'client' && searched && (
        clientReport
          ? <EvalClientReport agents={rows} report={clientReport} month={reportMonth} campaignIds={reportCampaignIds} />
          : <Empty description="Pick at least one campaign and search to see the Client Report." style={{ marginTop: 40 }} />
      )}

      {!loading && activeView === 'calendar' && searched && (
        <CalendarView rows={calRows} month={form.getFieldValue('month')} agentInfoMap={agentInfoMap} currentUser={currentUser} />
      )}

      <Modal maskClosable={false}
        title={condAgent ? `Find Qualified Calls — ${condAgent.DisplayName}` : 'Find Qualified Calls'}
        open={condModal}
        onCancel={() => setCondModal(false)}
        footer={null}
        width={480}
      >
        {condLoading ? (
          <div style={{ textAlign: 'center', padding: 32 }}><Spin /></div>
        ) : conditions.length === 0 ? (
          <Empty
            description="No active call conditions for this agent's campaigns."
            style={{ padding: '24px 0' }}
          >
            <Button type="primary" onClick={() => handleOpenSearch(null)}>
              Search without condition filter
            </Button>
          </Empty>
        ) : (
          <>
            <List
              size="small"
              dataSource={conditions}
              renderItem={(cond) => (
                <List.Item
                  style={{ cursor: 'pointer', padding: '10px 0' }}
                  onClick={() => handleOpenSearch(cond)}
                >
                  <List.Item.Meta
                    title={<Typography.Link>{cond.Title}</Typography.Link>}
                    description={cond.CampaignName || '—'}
                  />
                </List.Item>
              )}
            />
            <div style={{ borderTop: '1px solid var(--ant-color-split)', paddingTop: 12, marginTop: 4, textAlign: 'right' }}>
              <Typography.Link style={{ fontSize: 12 }} onClick={() => handleOpenSearch(null)}>
                Search without condition filter
              </Typography.Link>
            </div>
          </>
        )}
      </Modal>

      <Drawer
        title={
          evalContext
            ? `Evals — ${evalContext.agentName}${evalContext.campaign ? ` — ${evalContext.campaign}` : ''} — ${evalContext.monthLabel}${evalContext.statusLabel ? ` (${evalContext.statusLabel})` : ''}`
            : 'Evaluations'
        }
        open={evalDrawerOpen}
        onClose={() => setEvalDrawerOpen(false)}
        width="75vw"
        destroyOnClose
      >
        {evalLoading ? (
          <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
        ) : evalList.length === 0 ? (
          <Empty description="No evaluations found." style={{ marginTop: 40 }} />
        ) : (
          <Table
            rowKey="ID"
            size="small"
            dataSource={evalList}
            pagination={{ defaultPageSize: 30, hideOnSinglePage: true, showTotal: (t) => `${t} evals` }}
            scroll={{ x: 'max-content' }}
            columns={[
              {
                key: 'open',
                width: 48,
                align: 'center',
                render: (_, r) => (
                  <Button
                    type="text"
                    size="small"
                    icon={<EyeOutlined />}
                    title="Open eval"
                    onClick={() => window.open(`/cases/${r.ID}`, '_blank')}
                  />
                ),
              },
              {
                title: 'Contact ID',
                dataIndex: 'ContactID',
                key: 'ContactID',
                width: 110,
              },
              { title: 'External Ref', dataIndex: 'ExternalRef', key: 'ExternalRef', width: 120 },
              {
                title: 'Status',
                dataIndex: 'Status',
                key: 'Status',
                width: 110,
                render: (v) => {
                  const COLOR = { assigned: 'default', in_progress: 'blue', completed: 'green', rescore_requested: 'purple' };
                  const LABEL = { assigned: 'Draft', in_progress: 'In Progress', completed: 'Completed', rescore_requested: 'Disputed' };
                  return <Tag color={COLOR[v] || 'default'}>{LABEL[v] || v}</Tag>;
                },
              },
              { title: 'Assigned To', dataIndex: 'AssignedToName', key: 'AssignedToName', width: 130 },
              { title: 'Form', dataIndex: 'FormName', key: 'FormName', width: 160 },
              { title: 'Call Date', dataIndex: 'CallDate', key: 'CallDate', width: 100 },
              { title: 'Agent', dataIndex: 'AgentName', key: 'AgentName', width: 140 },
              { title: 'Campaign', dataIndex: 'Campaign', key: 'Campaign', width: 150 },
              { title: 'Skill', dataIndex: 'Skill', key: 'Skill', width: 140 },
              {
                title: 'Score',
                dataIndex: 'Score',
                key: 'Score',
                width: 75,
                align: 'right',
                render: (v) => v != null
                  ? <Text style={{ color: scoreColor(v), fontWeight: 600 }}>{v}%</Text>
                  : <span style={{ color: '#bfbfbf' }}>—</span>,
              },
            ]}
          />
        )}
      </Drawer>
    </div>
  );
}
